<script setup lang="ts">
/**
 * Per-aggregator buy_prices editor for Call entries.
 *
 * Wire format: `{ "<aggregator_id>": { phone_rent, income_calls, outcome_calls } }`.
 * Surface as `BuyPriceEntry<CallPrices>[]` for DevExtreme.
 */
import { computed, ref, watch } from 'vue';
import { DxButton } from 'devextreme-vue/button';
import { DxNumberBox } from 'devextreme-vue/number-box';
import { LookupBox, getAggregatorsLookup } from '@botto/core';
import type { BuyPriceEntry } from '../../api/normalizers';
import type { CallPrices } from '../../api/types';

const props = defineProps<{
  modelValue: BuyPriceEntry<CallPrices>[];
}>();
const emit = defineEmits<{
  (e: 'update:modelValue', v: BuyPriceEntry<CallPrices>[]): void;
}>();

const entries = ref<BuyPriceEntry<CallPrices>[]>([]);

watch(
  () => props.modelValue,
  (v) => {
    entries.value = v.map((it) => ({
      aggregator_id: it.aggregator_id,
      payload: { ...(it.payload ?? { phone_rent: 0, income_calls: 0, outcome_calls: 0 }) },
    }));
  },
  { immediate: true, deep: true },
);

function pushUpdate(): void {
  emit('update:modelValue', entries.value);
}

const aggregatorsLookup = getAggregatorsLookup();
const newAggregatorId = ref<number | null>(null);

const usedIds = computed(() => new Set(entries.value.map((e) => e.aggregator_id)));
const availableAggregators = computed(() =>
  aggregatorsLookup.items.value.filter((a) => !usedIds.value.has(a.id)),
);
const filteredAggregatorLookup = computed(() => ({
  ...aggregatorsLookup,
  items: availableAggregators,
}));

function addAggregator(): void {
  if (newAggregatorId.value == null) return;
  if (usedIds.value.has(newAggregatorId.value)) return;
  entries.value.push({
    aggregator_id: newAggregatorId.value,
    payload: { phone_rent: 0, income_calls: 0, outcome_calls: 0 },
  });
  newAggregatorId.value = null;
  pushUpdate();
}

function removeAggregator(aggregatorId: number): void {
  entries.value = entries.value.filter((e) => e.aggregator_id !== aggregatorId);
  pushUpdate();
}

function aggregatorLabel(id: number): string {
  return aggregatorsLookup.labelOf(id);
}
</script>

<template>
  <div class="buy-call-editor">
    <div class="bce-add-row">
      <span class="bce-add-label">Добавить агрегатора:</span>
      <LookupBox
        v-model="newAggregatorId"
        :lookup="filteredAggregatorLookup as unknown as ReturnType<typeof getAggregatorsLookup>"
        placeholder="Выберите агрегатора"
      />
      <DxButton icon="add" text="Добавить" type="default" styling-mode="contained" :disabled="!newAggregatorId" @click="addAggregator" />
    </div>

    <div v-if="entries.length === 0" class="bce-empty">Закупочных цен нет.</div>

    <div v-for="entry in entries" :key="entry.aggregator_id" class="bce-card">
      <div class="bce-card-header">
        <strong>{{ aggregatorLabel(entry.aggregator_id) }}</strong>
        <DxButton icon="trash" hint="Удалить" styling-mode="text" @click="removeAggregator(entry.aggregator_id)" />
      </div>
      <div class="bce-fields">
        <label class="bce-field">
          <span>Аренда номера</span>
          <DxNumberBox v-model:value="entry.payload.phone_rent" @value-changed="pushUpdate" />
        </label>
        <label class="bce-field">
          <span>Входящие</span>
          <DxNumberBox v-model:value="entry.payload.income_calls" @value-changed="pushUpdate" />
        </label>
        <label class="bce-field">
          <span>Исходящие</span>
          <DxNumberBox v-model:value="entry.payload.outcome_calls" @value-changed="pushUpdate" />
        </label>
      </div>
    </div>
  </div>
</template>

<style scoped>
.buy-call-editor { display: flex; flex-direction: column; gap: 12px; }
.bce-add-row { display: flex; align-items: center; gap: 8px; padding: 8px 0; }
.bce-add-label { font-size: 13px; color: #555; }
.bce-empty { color: #9ca3af; padding: 12px; text-align: center; border: 1px dashed #e5e7eb; border-radius: 4px; }
.bce-card { border: 1px solid #e5e7eb; border-radius: 4px; padding: 8px 12px; }
.bce-card-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.bce-fields { display: flex; gap: 16px; flex-wrap: wrap; }
.bce-field { display: flex; flex-direction: column; gap: 4px; font-size: 13px; min-width: 180px; }
</style>
